import { AccessBehaviorProps } from "../AccessGate.types";
import { canInjectDisabled } from "../../benefits/components/utils";
import {
    DisabledCloneWrap,
    OverlayWrap,
    TooltipWrap,
} from "../../benefits/components/wrappers/BenefitWrapper";

/**
 * Config accepted by DisabledBehavior through `behaviorConfig`.
 *
 * - `tooltip`       – text shown on hover when disabled.
 * - `overlayLabel`  – text shown inside the overlay fallback.
 * - `overlay`       – whether to show the blur overlay when the child
 *                     does not support a `disabled` prop (default: true).
 * - `sx` / `overlaySx` – style overrides forwarded to wrappers.
 */
export type DisabledBehaviorConfig = {
    tooltip?: string;
    overlayLabel?: string;
    overlay?: boolean;
    sx?: Record<string, unknown>;
    overlaySx?: Record<string, unknown>;
};

/**
 * Behavior that disables the children when access is blocked.
 *
 * - If the child element supports a `disabled` prop (buttons, inputs,
 *   MUI components), it clones the element with `disabled: true`.
 * - Otherwise, it wraps the child in a blur overlay.
 *
 * @example
 * <AccessGate
 *   strategy={access}
 *   behavior={DisabledBehavior}
 *   behaviorConfig={{ tooltip: "Upgrade required" }}
 * >
 *   <Button>Add Area</Button>
 * </AccessGate>
 */
export function DisabledBehavior({ children, config }: AccessBehaviorProps) {
    const {
        tooltip = "",
        overlayLabel = "",
        overlay = true,
        sx,
        overlaySx,
    } = (config ?? {}) as DisabledBehaviorConfig;

    const tooltipEnabled = !!tooltip;

    if (canInjectDisabled(children)) {
        return (
            <TooltipWrap enabled={tooltipEnabled} title={tooltip} display="inline-flex" sx={sx}>
                <DisabledCloneWrap child={children} sx={sx} />
            </TooltipWrap>
        );
    }

    return (
        <TooltipWrap enabled={tooltipEnabled} title={tooltip} display="block" sx={sx}>
            <OverlayWrap overlay={overlay} label={overlayLabel} sx={sx} overlaySx={overlaySx}>
                {children}
            </OverlayWrap>
        </TooltipWrap>
    );
}
